import { normalCdfGeneral, normalInvGeneral, normalPdf } from '../../../shared/normal-dist-math.util';
import { NormalDistributionChartParams, ShadedRegion, TopLabelConfig } from './normal-distribution-chart.model';

// ---------------------------------------------------------------------------
// Color constants
// ---------------------------------------------------------------------------

const INDIGO = '#6366f1';
const PINK = '#ec4899';
const NEUTRAL_300 = '#b2b1ae';
const NEUTRAL_500 = '#706f6d';
const PINK_SHADE = 'rgba(236, 72, 153, 0.25)';
const SKY_SHADE = 'rgba(14, 165, 233, 0.25)';

// ---------------------------------------------------------------------------
// Preset types (discriminated union)
// ---------------------------------------------------------------------------

interface PresetBase {
  mu: number;
  sigma: number;
  gridRight?: number;
}

export interface BasicPreset extends PresetBase {
  preset: 'basic';
  areaFill?: boolean;
  showYAxis?: boolean;
}

export interface CompareMeansPreset {
  preset: 'compare-means';
  mu1: number;
  mu2: number;
  sigma: number;
  gridRight?: number;
}

export interface CompareSpreadsPreset {
  preset: 'compare-spreads';
  mu: number;
  sigma1: number;
  sigma2: number;
  gridRight?: number;
}

export interface LeftTailPreset extends PresetBase {
  preset: 'left-tail';
  boundary: number;
  showProbability?: boolean;
}

export interface RightTailPreset extends PresetBase {
  preset: 'right-tail';
  boundary: number;
  showProbability?: boolean;
}

export interface BetweenPreset extends PresetBase {
  preset: 'between';
  lower: number;
  upper: number;
  showProbability?: boolean;
}

export interface TwoTailPreset extends PresetBase {
  preset: 'two-tail';
  lower: number;
  upper: number;
}

export interface EmpiricalRulePreset extends PresetBase {
  preset: 'empirical-rule';
}

export interface InversePreset extends PresetBase {
  preset: 'inverse';
  p: number;
  tail: 'left' | 'right';
}

export interface CentralIntervalPreset extends PresetBase {
  preset: 'central-interval';
  centralP: number;
}

export interface ConceptualPreset {
  preset: 'conceptual';
  type: 'left' | 'right' | 'between' | 'two-tail';
}

export type NormalChartPreset =
  | BasicPreset
  | CompareMeansPreset
  | CompareSpreadsPreset
  | LeftTailPreset
  | RightTailPreset
  | BetweenPreset
  | TwoTailPreset
  | EmpiricalRulePreset
  | InversePreset
  | CentralIntervalPreset
  | ConceptualPreset;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function autoRange(mu: number, sigma: number): { xMin: number; xMax: number } {
  return {
    xMin: Math.floor(mu - 4 * sigma),
    xMax: Math.ceil(mu + 4 * sigma),
  };
}

function peak(mu: number, sigma: number): number {
  return normalPdf(mu, mu, sigma);
}

function probabilityAnnotation(
  mu: number,
  sigma: number,
  xCenter: number,
  text: string,
  color?: string,
): { x: number; y: number; text: string; color?: string } {
  return { x: xCenter, y: 0.4 * peak(mu, sigma), text, ...(color ? { color } : {}) };
}

function formatP(p: number): string {
  const rounded = Math.round(p * 1e6) / 1e6;
  const str = rounded.toPrecision(3);
  return str.replace(/0+$/, '').replace(/\.$/, '');
}

// ---------------------------------------------------------------------------
// resolvePreset
// ---------------------------------------------------------------------------

export function resolvePreset(preset: NormalChartPreset): NormalDistributionChartParams {
  const base: Pick<NormalDistributionChartParams, 'gridRight'> = {
    gridRight: ('gridRight' in preset ? preset.gridRight : undefined) ?? 20,
  };

  switch (preset.preset) {
    case 'basic':
      return resolveBasic(preset, base);
    case 'compare-means':
      return resolveCompareMeans(preset, base);
    case 'compare-spreads':
      return resolveCompareSpreads(preset, base);
    case 'left-tail':
      return resolveLeftTail(preset, base);
    case 'right-tail':
      return resolveRightTail(preset, base);
    case 'between':
      return resolveBetween(preset, base);
    case 'two-tail':
      return resolveTwoTail(preset, base);
    case 'empirical-rule':
      return resolveEmpiricalRule(preset, base);
    case 'inverse':
      return resolveInverse(preset, base);
    case 'central-interval':
      return resolveCentralInterval(preset, base);
    case 'conceptual':
      return resolveConceptual(preset, base);
  }
}

// ---------------------------------------------------------------------------
// Individual resolvers
// ---------------------------------------------------------------------------

function resolveBasic(
  p: BasicPreset,
  base: Pick<NormalDistributionChartParams, 'gridRight'>,
): NormalDistributionChartParams {
  const { xMin, xMax } = autoRange(p.mu, p.sigma);
  return {
    ...base,
    curves: [{ mu: p.mu, sigma: p.sigma, color: INDIGO, areaFill: p.areaFill }],
    xMin,
    xMax,
    showYAxis: p.showYAxis,
  };
}

function resolveCompareMeans(
  p: CompareMeansPreset,
  base: Pick<NormalDistributionChartParams, 'gridRight'>,
): NormalDistributionChartParams {
  const lo = Math.min(p.mu1, p.mu2);
  const hi = Math.max(p.mu1, p.mu2);
  const xMin = Math.floor(lo - 4 * p.sigma);
  const xMax = Math.ceil(hi + 4 * p.sigma);
  return {
    ...base,
    curves: [
      { mu: p.mu1, sigma: p.sigma, color: PINK, lineStyle: 'solid' },
      { mu: p.mu2, sigma: p.sigma, color: INDIGO, lineStyle: 'dashed' },
    ],
    xMin,
    xMax,
  };
}

function resolveCompareSpreads(
  p: CompareSpreadsPreset,
  base: Pick<NormalDistributionChartParams, 'gridRight'>,
): NormalDistributionChartParams {
  const maxSigma = Math.max(p.sigma1, p.sigma2);
  const xMin = Math.floor(p.mu - 4 * maxSigma);
  const xMax = Math.ceil(p.mu + 4 * maxSigma);
  return {
    ...base,
    curves: [
      { mu: p.mu, sigma: p.sigma1, color: PINK, lineStyle: 'solid' },
      { mu: p.mu, sigma: p.sigma2, color: INDIGO, lineStyle: 'dashed' },
    ],
    xMin,
    xMax,
  };
}

function resolveLeftTail(
  p: LeftTailPreset,
  base: Pick<NormalDistributionChartParams, 'gridRight'>,
): NormalDistributionChartParams {
  const { xMin, xMax } = autoRange(p.mu, p.sigma);
  const shadedRegions: ShadedRegion[] = [{ from: xMin, to: p.boundary }];
  const topLabels: TopLabelConfig[] = [{ value: p.boundary, text: String(p.boundary) }];
  const annotations = p.showProbability
    ? [
        probabilityAnnotation(
          p.mu,
          p.sigma,
          (xMin + p.boundary) / 2,
          formatP(normalCdfGeneral(p.boundary, p.mu, p.sigma)),
        ),
      ]
    : undefined;

  return {
    ...base,
    curves: [{ mu: p.mu, sigma: p.sigma, color: INDIGO }],
    xMin,
    xMax,
    shadedRegions,
    topLabels,
    annotations,
  };
}

function resolveRightTail(
  p: RightTailPreset,
  base: Pick<NormalDistributionChartParams, 'gridRight'>,
): NormalDistributionChartParams {
  const { xMin, xMax } = autoRange(p.mu, p.sigma);
  const shadedRegions: ShadedRegion[] = [{ from: p.boundary, to: xMax }];
  const topLabels: TopLabelConfig[] = [{ value: p.boundary, text: String(p.boundary) }];
  const annotations = p.showProbability
    ? [
        probabilityAnnotation(
          p.mu,
          p.sigma,
          (p.boundary + xMax) / 2,
          formatP(1 - normalCdfGeneral(p.boundary, p.mu, p.sigma)),
        ),
      ]
    : undefined;

  return {
    ...base,
    curves: [{ mu: p.mu, sigma: p.sigma, color: INDIGO }],
    xMin,
    xMax,
    shadedRegions,
    topLabels,
    annotations,
  };
}

function resolveBetween(
  p: BetweenPreset,
  base: Pick<NormalDistributionChartParams, 'gridRight'>,
): NormalDistributionChartParams {
  const { xMin, xMax } = autoRange(p.mu, p.sigma);
  const shadedRegions: ShadedRegion[] = [{ from: p.lower, to: p.upper }];
  const topLabels: TopLabelConfig[] = [
    { value: p.lower, text: String(p.lower) },
    { value: p.upper, text: String(p.upper) },
  ];
  const annotations = p.showProbability
    ? [
        probabilityAnnotation(
          p.mu,
          p.sigma,
          (p.lower + p.upper) / 2,
          formatP(normalCdfGeneral(p.upper, p.mu, p.sigma) - normalCdfGeneral(p.lower, p.mu, p.sigma)),
        ),
      ]
    : undefined;

  return {
    ...base,
    curves: [{ mu: p.mu, sigma: p.sigma, color: INDIGO }],
    xMin,
    xMax,
    shadedRegions,
    topLabels,
    annotations,
  };
}

function resolveTwoTail(
  p: TwoTailPreset,
  base: Pick<NormalDistributionChartParams, 'gridRight'>,
): NormalDistributionChartParams {
  const { xMin, xMax } = autoRange(p.mu, p.sigma);
  return {
    ...base,
    curves: [{ mu: p.mu, sigma: p.sigma, color: INDIGO }],
    xMin,
    xMax,
    shadedRegions: [
      { from: xMin, to: p.lower },
      { from: p.upper, to: xMax },
    ],
    topLabels: [
      { value: p.lower, text: String(p.lower) },
      { value: p.upper, text: String(p.upper) },
    ],
  };
}

function resolveEmpiricalRule(
  p: EmpiricalRulePreset,
  base: Pick<NormalDistributionChartParams, 'gridRight'>,
): NormalDistributionChartParams {
  const { xMin, xMax } = autoRange(p.mu, p.sigma);
  return {
    ...base,
    curves: [{ mu: p.mu, sigma: p.sigma }],
    xMin,
    xMax,
    showEmpiricalRule: true,
  };
}

function resolveInverse(
  p: InversePreset,
  base: Pick<NormalDistributionChartParams, 'gridRight'>,
): NormalDistributionChartParams {
  const { xMin, xMax } = autoRange(p.mu, p.sigma);
  const k = normalInvGeneral(p.tail === 'left' ? p.p : 1 - p.p, p.mu, p.sigma);
  const kRounded = Math.round(k * 1000) / 1000;
  const kLabel = String(kRounded);

  const shadedRegions: ShadedRegion[] =
    p.tail === 'left' ? [{ from: xMin, to: k, color: PINK_SHADE }] : [{ from: k, to: xMax, color: PINK_SHADE }];

  const complementRegion: ShadedRegion =
    p.tail === 'left' ? { from: k, to: xMax, color: SKY_SHADE } : { from: xMin, to: k, color: SKY_SHADE };
  shadedRegions.push(complementRegion);

  const topLabels: TopLabelConfig[] = [
    { value: k, text: kLabel, color: NEUTRAL_500, lineType: 'dashed', extent: 'full' },
  ];

  const pText = formatP(p.p);
  const qText = formatP(1 - p.p);
  const pinkColor = PINK;
  const skyColor = '#0ea5e9';

  const annotations =
    p.tail === 'left'
      ? [
          probabilityAnnotation(p.mu, p.sigma, (xMin + k) / 2, pText, pinkColor),
          probabilityAnnotation(p.mu, p.sigma, (k + xMax) / 2, qText, skyColor),
        ]
      : [
          probabilityAnnotation(p.mu, p.sigma, (xMin + k) / 2, qText, skyColor),
          probabilityAnnotation(p.mu, p.sigma, (k + xMax) / 2, pText, pinkColor),
        ];

  const title = {
    text: `k = ${kLabel}`,
    color: NEUTRAL_500,
  };

  return {
    ...base,
    curves: [{ mu: p.mu, sigma: p.sigma, color: NEUTRAL_300 }],
    xMin,
    xMax,
    shadedRegions,
    topLabels,
    annotations,
    title,
  };
}

function resolveCentralInterval(
  p: CentralIntervalPreset,
  base: Pick<NormalDistributionChartParams, 'gridRight'>,
): NormalDistributionChartParams {
  const { xMin, xMax } = autoRange(p.mu, p.sigma);
  const alpha = (1 - p.centralP) / 2;
  const lower = normalInvGeneral(alpha, p.mu, p.sigma);
  const upper = normalInvGeneral(1 - alpha, p.mu, p.sigma);
  const lowerRounded = Math.round(lower * 1000) / 1000;
  const upperRounded = Math.round(upper * 1000) / 1000;

  return {
    ...base,
    curves: [{ mu: p.mu, sigma: p.sigma, color: NEUTRAL_300 }],
    xMin,
    xMax,
    shadedRegions: [
      { from: xMin, to: lower, color: SKY_SHADE },
      { from: lower, to: upper, color: PINK_SHADE },
      { from: upper, to: xMax, color: SKY_SHADE },
    ],
    topLabels: [
      { value: lower, text: String(lowerRounded), color: NEUTRAL_500, lineType: 'dashed', extent: 'full' },
      { value: upper, text: String(upperRounded), color: NEUTRAL_500, lineType: 'dashed', extent: 'full' },
    ],
    annotations: [
      probabilityAnnotation(p.mu, p.sigma, (xMin + lower) / 2, formatP(alpha), '#0ea5e9'),
      probabilityAnnotation(p.mu, p.sigma, (lower + upper) / 2, formatP(p.centralP), PINK),
      probabilityAnnotation(p.mu, p.sigma, (upper + xMax) / 2, formatP(alpha), '#0ea5e9'),
    ],
    title: {
      text: `${String(lowerRounded)} \\leq X \\leq ${String(upperRounded)}`,
      color: NEUTRAL_500,
    },
  };
}

function resolveConceptual(
  p: ConceptualPreset,
  base: Pick<NormalDistributionChartParams, 'gridRight'>,
): NormalDistributionChartParams {
  const mu = 0;
  const sigma = 1;
  const { xMin, xMax } = autoRange(mu, sigma);

  const common: NormalDistributionChartParams = {
    ...base,
    curves: [{ mu, sigma, color: INDIGO }],
    xMin,
    xMax,
    showAxisLabels: false,
  };

  switch (p.type) {
    case 'left':
      return {
        ...common,
        shadedRegions: [{ from: xMin, to: -1 }],
        topLabels: [{ value: -1, text: 'a' }],
      };
    case 'right':
      return {
        ...common,
        shadedRegions: [{ from: 1, to: xMax }],
        topLabels: [{ value: 1, text: 'a' }],
      };
    case 'between':
      return {
        ...common,
        shadedRegions: [{ from: -1, to: 1 }],
        topLabels: [
          { value: -1, text: 'a' },
          { value: 1, text: 'b' },
        ],
      };
    case 'two-tail':
      return {
        ...common,
        shadedRegions: [
          { from: xMin, to: -1.5 },
          { from: 1.5, to: xMax },
        ],
        topLabels: [
          { value: -1.5, text: 'a' },
          { value: 1.5, text: 'b' },
        ],
      };
  }
}
